/**
 * Date and time formatting utilities for Trinidad and Tobago timezone
 * All dates and times displayed in the admin app should use these utilities
 */

import {
  formatTrinidadDate,
  formatTrinidadTime,
  getTrinidadDayEnd,
  getTrinidadDayStart,
  getTrinidadDateStringUTC,
  getTrinidadTime,
} from '@neighbourhood-munchies/shared-utils';

// Re-export so all admin-web components get Trinidad time utilities
// from one place rather than importing shared-utils directly
export { getTrinidadTime };

/**
 * Format a date for display in Trinidad timezone
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string (e.g., "Jan 15, 2025")
 */
export function formatDate(dateString: string | Date): string {
  return formatTrinidadDate(dateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date with full month name
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string (e.g., "January 15, 2025")
 */
export function formatDateLong(dateString: string | Date): string {
  return formatTrinidadDate(dateString, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a date in numeric format
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string (e.g., "01/15/2025")
 */
export function formatDateNumeric(dateString: string | Date): string {
  return formatTrinidadDate(dateString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Format time for display in Trinidad timezone
 * @param dateString - ISO date string or Date object
 * @returns Formatted time string (e.g., "2:30 PM")
 */
export function formatTime(dateString: string | Date): string {
  return formatTrinidadTime(dateString, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format time in 24-hour format
 * @param dateString - ISO date string or Date object
 * @returns Formatted time string (e.g., "14:30")
 */
export function formatTime24(dateString: string | Date): string {
  return formatTrinidadTime(dateString, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Format date and time together
 * @param dateString - ISO date string or Date object
 * @returns Formatted date and time string (e.g., "Jan 15, 2025, 2:30 PM")
 */
export function formatDateTime(dateString: string | Date): string {
  const date = formatDate(dateString);
  const time = formatTime(dateString);
  return `${date}, ${time}`;
}

/**
 * Format date and time with full month name
 * @param dateString - ISO date string or Date object
 * @returns Formatted date and time string (e.g., "January 15, 2025 at 2:30 PM")
 */
export function formatDateTimeLong(dateString: string | Date): string {
  const date = formatDateLong(dateString);
  const time = formatTime(dateString);
  return `${date} at ${time}`;
}

/**
 * Get current Trinidad time
 * @returns Current Date object in Trinidad timezone
 */
export function getCurrentTime(): Date {
  return getTrinidadTime();
}

/**
 * Get today's date range in Trinidad timezone
 * @returns Object with start and end ISO strings for today
 */
export function getTodayRange(): { start: string; end: string } {
  return {
    start: getTrinidadDayStart(),
    end: getTrinidadDayEnd(),
  };
}

/**
 * Get today's date in YYYY-MM-DD format for Trinidad timezone
 * @returns Date string (e.g., "2025-01-15")
 */
export function getTodayDateString(): string {
  // Use 'sv' locale with Intl.DateTimeFormat — guaranteed to return YYYY-MM-DD regardless of environment
  return getTrinidadDateStringUTC();
}
